/**
 * Story 054 — External IDs sub-form.
 *
 * Inline repeating list of `{provider, id}` rows, driven by React Hook
 * Form's `useFieldArray`. PATCH sends the full replaced array; empty
 * rows are silently dropped on submit (UX brief §6.3).
 */
import { type ReactElement } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  useFieldArray,
  type Control,
  type UseFormRegister,
} from "react-hook-form";
import type { ExternalId } from "../queries/index.js";
import { CircleMinusIcon, PlusIcon } from "@aeriosiq/ui/icons";

export interface ExternalIdsFieldValues {
  externalIds: ExternalId[];
}

export interface ExternalIdsFieldProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>;
  readOnly?: boolean;
}

export function ExternalIdsField({
  control,
  register,
  readOnly = false,
}: ExternalIdsFieldProps): ReactElement {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "externalIds",
  });

  return (
    <Box>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        External IDs
      </Typography>
      <Stack spacing={1}>
        {fields.length === 0 ? (
          <Typography
            variant="body2"
            color="text.tertiary"
            sx={{ fontStyle: "italic" }}
          >
            No external IDs.
          </Typography>
        ) : (
          fields.map((field, index) => (
            <Stack
              key={field.id}
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <TextField
                {...register(`externalIds.${String(index)}.provider`)}
                placeholder="Provider"
                size="small"
                sx={{ width: 200 }}
                slotProps={{
                  htmlInput: {
                    "aria-label": `External ID ${String(index + 1)} provider`,
                    ...(readOnly ? { readOnly: true } : {}),
                  },
                }}
              />
              <TextField
                {...register(`externalIds.${String(index)}.id`)}
                placeholder="ID"
                size="small"
                sx={{ flex: 1 }}
                slotProps={{
                  htmlInput: {
                    "aria-label": `External ID ${String(index + 1)} value`,
                    ...(readOnly ? { readOnly: true } : {}),
                  },
                }}
              />
              {!readOnly ? (
                <IconButton
                  aria-label={`Remove external ID ${String(index + 1)}`}
                  size="small"
                  onClick={() => {
                    remove(index);
                  }}
                >
                  <CircleMinusIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Stack>
          ))
        )}
      </Stack>
      {!readOnly ? (
        <Button
          size="small"
          variant="text"
          startIcon={<PlusIcon fontSize="small" />}
          onClick={() => {
            append({ provider: "", id: "" });
          }}
          sx={{ mt: 1 }}
        >
          Add external ID
        </Button>
      ) : null}
    </Box>
  );
}
